import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import Selector from '../../components/shared/Selector/Selector';
import UserSelector from '../../components/shared/UserSelector/UserSelector';
import FileUpload, { ExistingFile, NewFile } from '../../components/shared/FileUpload/FileUpload';
import { Loading } from '../../components/shared';
import { ServiceTasks, TaskStatus } from '../../services/service-tasks';
import appState from '../../state/AppStateContainer';
import { FileScope } from '../../util/appEnum';
import {
  dueDateFromPick,
  validateActivity,
  toTaskFormData,
  toExistingFiles,
  toProjectOptions,
  ActivityField,
  Option,
} from './activityForm';
import styles from './ActivityModal.module.scss';

type Props = {
  taskId?: string;
  initialTitle?: string;
  initialDescription?: string;
  lockedProject?: Option;
  onSaved?: () => void;
  onClose: () => void;
};

// Must match the failures services/axiosHelper.js already toasts, or the user gets two notices or none.
const hasGlobalNotice = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return (
    error.message === 'Network Error' ||
    status === 401 ||
    status === 415 ||
    (status === 403 && !appState.isSupplierRole())
  );
};

const ActivityModal: FC<Props> = ({
  taskId,
  initialTitle = '',
  initialDescription = '',
  lockedProject,
  onSaved,
  onClose,
}) => {
  const [t] = useTranslation('dashboard');
  const isEdit = Boolean(taskId);
  const myId = appState.getUserId() ?? '';
  const myName = appState.getLoggedInUserDetail()?.name ?? '';

  const [loading, setLoading] = useState(isEdit);
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [dueDate, setDueDate] = useState<Date | null>(
    isEdit ? null : moment().add(1, 'day').startOf('day').toDate(),
  );
  const [project, setProject] = useState<Option | null>(lockedProject ?? null);
  const [projectOptions, setProjectOptions] = useState<Option[]>(
    lockedProject ? [lockedProject] : [],
  );
  const [assignees, setAssignees] = useState<Option[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<Option[]>([]);
  const [status, setStatus] = useState<TaskStatus>('inProgress');
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Only a new activity or a user-picked project gets the default assignee; a loaded task keeps its own.
  const applyDefaultAssignee = useRef(!isEdit);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const statusOptions: Option<TaskStatus>[] = [
    { value: 'inProgress', label: t('messages.statusInProgress', 'Pågående') },
    { value: 'complete', label: t('messages.statusComplete', 'Slutförd') },
  ];

  const optionsLoadFailed = t(
    'messages.optionsLoadFailed',
    'Projekt eller användare kunde inte hämtas',
  );
  const loadFailed = t('messages.loadFailed', 'Aktiviteten kunde inte hämtas');

  const isProjectLocked = Boolean(lockedProject);
  useEffect(() => {
    if (isProjectLocked) return;
    let cancelled = false;
    ServiceTasks.getProjectsForTasks()
      .then(projects => {
        if (!cancelled) setProjectOptions(toProjectOptions(projects));
      })
      .catch((error: unknown) => {
        if (!cancelled && !hasGlobalNotice(error)) NotificationManager.error(optionsLoadFailed);
      });
    return () => {
      cancelled = true;
    };
  }, [isProjectLocked, optionsLoadFailed]);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    ServiceTasks.getTask(taskId)
      .then(task => {
        if (cancelled) return;
        setTitle(task.title);
        setDescription(task.description ?? '');
        setDueDate(task.dueDate ? new Date(task.dueDate) : null);
        setProject({ value: task.project._id, label: task.project.name ?? '' });
        setAssignees(task.assignTo.map(u => ({ value: u._id, label: u.name })));
        setStatus(task.status);
        setExistingFiles(toExistingFiles(task.attachments));
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (!hasGlobalNotice(error)) NotificationManager.error(loadFailed);
        onCloseRef.current();
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, loadFailed]);

  const projectId = project?.value;
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const showOptions = (options: Option[]) => {
      setAssigneeOptions(options);
      if (applyDefaultAssignee.current) {
        applyDefaultAssignee.current = false;
        const mine = options.find(o => o.value === myId);
        setAssignees(mine ? [mine] : []);
      }
    };
    ServiceTasks.getProjectUsers(projectId)
      .then(users => {
        if (!cancelled) showOptions(users.map(u => ({ value: u._id, label: u.name })));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (!hasGlobalNotice(error)) NotificationManager.error(optionsLoadFailed);
        showOptions([{ value: myId, label: myName }]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, myId, myName, optionsLoadFailed]);

  const draft = {
    title,
    description,
    dueDate,
    status,
    existingFiles,
    newFiles,
    projectId: project?.value ?? null,
    assignTo: assignees.map(a => a.value),
  };
  const missing = attempted ? validateActivity(draft) : [];
  const errorFor = (field: ActivityField) =>
    missing.includes(field) ? t('messages.errorRequired', 'Obligatoriskt fält') : undefined;

  const changeProject = (option: Option | null) => {
    if (option?.value === project?.value) return;
    applyDefaultAssignee.current = true;
    setProject(option);
    setAssignees([]);
    setAssigneeOptions([]);
  };

  const handleSubmit = async () => {
    setAttempted(true);
    if (validateActivity(draft).length > 0) return;
    setSubmitting(true);
    try {
      const formData = toTaskFormData(draft, taskId);
      await (isEdit ? ServiceTasks.updateTasks(formData) : ServiceTasks.addTasks(formData));
      NotificationManager.success(
        isEdit
          ? t('messages.activityUpdated', 'Aktivitet uppdaterad')
          : t('messages.activityCreated', 'Aktivitet skapad'),
        t('messages.saved', 'Sparad'),
      );
      onSaved?.();
      if (mounted.current) onClose();
    } catch (error) {
      if (!hasGlobalNotice(error))
        NotificationManager.error(t('messages.saveFailed', 'Aktiviteten kunde inte sparas'));
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  const heading = isEdit
    ? t('messages.editActivity', 'Redigera aktivitet')
    : t('messages.createActivity', 'Skapa aktivitet');

  return (
    <Modal
      title={heading}
      show={true}
      setShow={val => {
        if (!val) onClose();
      }}
      buttonInfo={{
        label: isEdit
          ? t('messages.save', 'Spara')
          : t('messages.createActivity', 'Skapa aktivitet'),
        action: handleSubmit,
        loaderShow: submitting,
        disabled: loading,
      }}
    >
      {loading ? (
        <Loading type="inline" />
      ) : (
        <div className={styles.container}>
          <Field
            label={t('messages.fieldTitle', 'Namn för aktivitet')}
            required
            value={title}
            onChange={(value: string) => setTitle(value)}
            errorMessage={errorFor('title')}
            style={{ fontSize: '14px' }}
          />
          <Field
            label={t('messages.fieldDescription', 'Beskrivning av aktivitet')}
            required
            value={description}
            onChange={(value: string) => setDescription(value)}
            errorMessage={errorFor('description')}
            multiline
            style={{ fontSize: '14px' }}
          />
          <DatePicker
            minDate={new Date()}
            label={t('messages.fieldDueDate', 'Förfallodatum')}
            required
            selected={dueDate ?? undefined}
            onChange={(date: Date) => setDueDate(current => dueDateFromPick(current, date))}
            errorMessage={errorFor('dueDate')}
            style={{ fontSize: '14px' }}
          />
          <Selector
            label={t('messages.fieldProject', 'Projekt')}
            required
            isSearchable
            virtualized
            options={projectOptions}
            value={projectOptions.find(o => o.value === project?.value) ?? project}
            isDisabled={isProjectLocked}
            onChange={changeProject}
            errorMessage={errorFor('projectId')}
          />
          <UserSelector
            label={t('messages.fieldAssignee', 'Tilldela aktivitet')}
            required
            isMulti
            isOverflow
            options={assigneeOptions}
            value={assignees}
            onChange={value => setAssignees([...(value ?? [])])}
            errorMessage={errorFor('assignTo')}
          />
          <Selector
            label={t('messages.fieldStatus', 'Status')}
            required
            options={isEdit ? statusOptions : [statusOptions[0]]}
            value={statusOptions.find(o => o.value === status) ?? statusOptions[0]}
            isDisabled={!isEdit}
            onChange={option => {
              if (option) setStatus(option.value);
            }}
          />
          <FileUpload
            existingFiles={existingFiles}
            setExistingFiles={setExistingFiles}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            scope={FileScope.TaskAttachment}
            scopeOptions={{ taskId }}
          />
        </div>
      )}
    </Modal>
  );
};

export default ActivityModal;
